import React, { useEffect, useState } from "react";
import ChevronDownSm from "@/assets/icons/ChevronDownSm.svg";

const HEADERS = ["", "Name", "Method", "Status", "Domain", "Size", "Time"];

interface NetworkLog {
  url: string;
  method: string;
  status: number;
  responseSize: number;
  duration: number;
  requestHeaders?: Array<{ name: string; value: string }>;
  responseHeaders?: Array<{ name: string; value: string }>;
  requestBody?: string;
  requestPayload?: string;
  responseBody?: string;
  timestamp: Date;
  resourceType?: string;
  fromCache?: boolean;
  mimeType?: string;
  statusText?: string;
  remoteIPAddress?: string;
  initiator?: string;
}

export default function ResizableTable({
  rows,
}: {
  rows: NetworkLog[];
}): JSX.Element {
  const [columnWidths, setColumnWidths] = useState<number[]>([
    24, 200, 54, 54, 55, 43, 38,
  ]);
  const [sortKey, setSortKey] = useState<"size" | "time" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selectedRow, setSelectedRow] = useState<NetworkLog | null>(null);
  const [activeTab, setActiveTab] = useState<
    "headers" | "payload" | "response"
  >("headers");
  const [sidePanelWidth, setSidePanelWidth] = useState(500);

  const SORTABLE_COLUMNS: Record<string, "size" | "time"> = {
    Size: "size",
    Time: "time",
  };

  const handleSort = (key: "size" | "time") => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedRows = sortKey
    ? [...rows].sort((a, b) => {
        const valA = (sortKey === "size" ? a.responseSize : a.duration) || 0;
        const valB = (sortKey === "size" ? b.responseSize : b.duration) || 0;
        return sortDir === "asc" ? valA - valB : valB - valA;
      })
    : rows;

  const MIN_COL = 50;
  const MIN_PANEL_WIDTH = 300;
  const MAX_PANEL_WIDTH = 800;

  const startResize = (index: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    try {
      (e.target as Element).setPointerCapture?.(e.pointerId);
    } catch {}

    const startX = e.clientX;
    const startWidths = columnWidths.slice();

    const onPointerMove = (ev: PointerEvent) => {
      const delta = ev.clientX - startX;
      if (index >= startWidths.length - 1) return;

      const leftStart = startWidths[index];
      const rightStart = startWidths[index + 1];

      let left = leftStart + delta;
      let right = rightStart - delta;

      if (left < MIN_COL) {
        left = MIN_COL;
        right = leftStart + rightStart - left;
      } else if (right < MIN_COL) {
        right = MIN_COL;
        left = leftStart + rightStart - right;
      }

      const next = startWidths.slice();
      next[index] = Math.round(left);
      next[index + 1] = Math.round(right);
      setColumnWidths(next);
    };

    const onPointerUp = () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      try {
        (e.target as Element).releasePointerCapture?.(e.pointerId);
      } catch {}
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };

  const startPanelResize = (e: React.PointerEvent) => {
    e.preventDefault();
    try {
      (e.target as Element).setPointerCapture?.(e.pointerId);
    } catch {}

    const startX = e.clientX;
    const startWidth = sidePanelWidth;

    const onPointerMove = (ev: PointerEvent) => {
      const delta = startX - ev.clientX; // reversed because we're resizing from the left edge
      let newWidth = startWidth + delta;

      // Clamp width
      if (newWidth < MIN_PANEL_WIDTH) {
        newWidth = MIN_PANEL_WIDTH;
      } else if (newWidth > MAX_PANEL_WIDTH) {
        newWidth = MAX_PANEL_WIDTH;
      }

      setSidePanelWidth(Math.round(newWidth));
    };

    const onPointerUp = () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      try {
        (e.target as Element).releasePointerCapture?.(e.pointerId);
      } catch {}
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };

  const formatBytes = (bytes: number) => {
    if (!bytes) return "0 B";
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${bytes} B`;
  };

  const formatDuration = (duration: number) => {
    if (!duration) return "0 ms";
    return duration >= 1000
      ? `${(duration / 1000).toFixed(2)} s`
      : `${duration} ms`;
  };

  const tryParseJSON = (str: string) => {
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  const getHostname = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  return (
    <div className="flex h-full">
      {/* Main Table */}
      <div className={`overflow-auto ${selectedRow ? "flex-1" : "w-full"}`}>
        <table className="table-fixed geist !text-[10px] leading-[16px] w-full border-collapse text-left">
          <colgroup>
            {columnWidths.map((w, i) => (
              <col key={i} style={{ width: `${w}px` }} />
            ))}
          </colgroup>

          <thead className="bg-gray-300 text-gray-800 select-none">
            <tr>
              {HEADERS.map((h, i) => {
                const sortableKey = SORTABLE_COLUMNS[h];
                return (
                  <th
                    key={i}
                    className={`relative text-[10px] ${
                      i === 1 ? "pr-[9px] !pl-6" : "px-[9px]"
                    } h-[24px] uppercase font-[400] ${sortableKey ? "cursor-pointer" : ""}`}
                    onClick={
                      sortableKey ? () => handleSort(sortableKey) : undefined
                    }
                  >
                    <div className="flex items-center gap-1">
                      <span>{h}</span>
                      {sortableKey && (
                        <img
                          src={ChevronDownSm}
                          alt={sortDir}
                          className="w-3 h-3 transition-all duration-200"
                          style={{
                            transform:
                              sortKey === sortableKey && sortDir === "asc"
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            opacity: sortKey === sortableKey ? 1 : 0,
                          }}
                        />
                      )}
                    </div>

                    {i < HEADERS.length - 1 && (
                      <div
                        role="separator"
                        aria-orientation="vertical"
                        onPointerDown={startResize(i)}
                        style={{ touchAction: "none" }}
                        className="absolute top-0 right-0 h-full flex items-center justify-center w-4 -mr-2 cursor-col-resize"
                      >
                        <div className="h-[16px] w-[2px] rounded-[10px] mx-auto bg-transparent hover:bg-gray-900" />
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="text-white overflow-scroll">
            {sortedRows.map((row: NetworkLog, i: number) => (
              <tr
                key={i}
                onClick={() => setSelectedRow(row)}
                className={`cursor-pointer my-[1px] ${
                  selectedRow === row
                    ? "bg-gray-400"
                    : i % 2 === 0
                      ? "bg-gray-300"
                      : "bg-transparent"
                }`}
              >
                <td className="px-[9px] !pl-[24px] h-[24px]">{i + 1}</td>
                <td className="px-[9px] !pl-6 truncate h-[24px]">
                  {row.url.split("/")[row.url.split("/").length - 1] || row.url}
                </td>
                <td className="px-[9px] truncate h-[24px]">{row.method}</td>
                <td className="px-[9px] truncate h-[24px]">{row.status}</td>
                <td className="px-[9px] truncate h-[24px]">
                  {getHostname(row.url)}
                </td>
                <td className="px-[9px] truncate h-[24px]">
                  {formatBytes(row.responseSize)}
                </td>
                <td className="px-[9px] truncate h-[24px]">
                  {formatDuration(row.duration)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Side Panel */}
      {selectedRow && (
        <div
          className="relative bg-gray-200 flex flex-col border-l-2 border-gray-400"
          style={{ width: `${sidePanelWidth}px` }}
        >
          <div
            role="separator"
            aria-orientation="vertical"
            onPointerDown={startPanelResize}
            style={{ touchAction: "none" }}
            className="absolute left-0 top-0 h-full w-4 -ml-2 cursor-col-resize flex items-center justify-center z-10"
          >
            <div className="h-full w-[2px] bg-transparent hover:bg-gray-900" />
          </div>

          <div className="flex items-center justify-between px-4 py-2 bg-gray-300 border-b border-gray-400">
            <h3 className="text-[10px] font-[400] text-gray-800 truncate flex-1 uppercase">
              Request Details
            </h3>
            <button
              onClick={() => setSelectedRow(null)}
              className="text-gray-800 transition-all hover:text-gray-900 text-xs ml-2 w-4 h-4 flex items-center justify-center"
            >
              ✕
            </button>
          </div>

          <div className="flex bg-gray-300 border-b border-gray-400">
            {["headers", "payload", "response"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as typeof activeTab)}
                className={`px-3 py-1.5 text-[10px] font-[400] uppercase ${
                  activeTab === tab ? "" : "text-gray-800"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-auto p-4 text-[10px] bg-gray-100">
            {activeTab === "headers" && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-gray-800 font-[500] mb-2 uppercase text-[9px]">
                    General
                  </h4>
                  <div className="space-y-1 text-white bg-gray-300 p-2 rounded">
                    <div className="flex">
                      <span className="w-32 text-white font-[500]">
                        Request URL:
                      </span>
                      <span className="flex-1 break-all">
                        {selectedRow.url}
                      </span>
                    </div>
                    <div className="flex">
                      <span className="w-32 text-white font-[500]">
                        Request Method:
                      </span>
                      <span>{selectedRow.method}</span>
                    </div>
                    <div className="flex">
                      <span className="w-32 text-white font-[500]">
                        Status Code:
                      </span>
                      <span>
                        {selectedRow.status} {selectedRow.statusText}
                      </span>
                    </div>
                    {selectedRow.remoteIPAddress && (
                      <div className="flex">
                        <span className="w-32 text-white font-[500]">
                          Remote Address:
                        </span>
                        <span>{selectedRow.remoteIPAddress}</span>
                      </div>
                    )}
                    {selectedRow.fromCache && (
                      <div className="flex">
                        <span className="w-32 text-white font-[500]">
                          From Cache:
                        </span>
                        <span>Yes</span>
                      </div>
                    )}
                  </div>
                </div>

                {selectedRow.responseHeaders &&
                  selectedRow.responseHeaders.length > 0 && (
                    <div>
                      <h4 className="text-gray-800 font-[500] mb-2 uppercase text-[9px]">
                        Response Headers
                      </h4>
                      <div className="space-y-0.5 text-white font-mono text-[9px] bg-gray-300 p-2 rounded">
                        {selectedRow.responseHeaders.map((header, i) => (
                          <div key={i} className="flex">
                            <span className="text-white min-w-[150px] font-[500]">
                              {header.name}:
                            </span>
                            <span className="ml-2 break-all">
                              {header.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {selectedRow.requestHeaders &&
                  selectedRow.requestHeaders.length > 0 && (
                    <div>
                      <h4 className="text-gray-800 font-[500] mb-2 uppercase text-[9px]">
                        Request Headers
                      </h4>
                      <div className="space-y-0.5 text-gray-700 font-mono text-[9px] bg-gray-300 p-2 rounded">
                        {selectedRow.requestHeaders.map((header, i) => (
                          <div key={i} className="flex">
                            <span className="text-white min-w-[150px] font-[500]">
                              {header.name}:
                            </span>
                            <span className="ml-2 break-all text-white">
                              {header.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}

            {activeTab === "payload" && (
              <div>
                <h4 className="text-gray-800 font-[500] mb-2 uppercase text-[9px]">
                  Request Payload
                </h4>
                {selectedRow.requestBody || selectedRow.requestPayload ? (
                  <pre className="bg-gray-300 p-3 rounded text-white overflow-auto font-mono text-[9px] border border-gray-300">
                    {JSON.stringify(
                      tryParseJSON(
                        selectedRow.requestBody ||
                          selectedRow.requestPayload ||
                          "",
                      ),
                      null,
                      2,
                    )}
                  </pre>
                ) : (
                  <p className="text-white italic bg-gray-300 p-3 rounded">
                    No request payload
                  </p>
                )}
              </div>
            )}

            {activeTab === "response" && (
              <div>
                <h4 className="text-gray-800 font-[500] mb-2 uppercase text-[9px]">
                  Response Body
                </h4>
                {selectedRow.responseBody ? (
                  <pre className="bg-gray-300 p-3 rounded text-white overflow-auto font-mono text-[9px] border border-gray-300">
                    {JSON.stringify(
                      tryParseJSON(selectedRow.responseBody),
                      null,
                      2,
                    )}
                  </pre>
                ) : (
                  <p className="text-white italic bg-gray-300 p-3 rounded">
                    No response body captured
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
